import { buildMarginal, resolveMonthlyFeePct } from './xirrFee';
import { applyRepaymentPeriodLimit } from './resolveEffectiveRepaymentPeriods';
import { resolveRepaymentRoasVector } from './extendRoasVectorForRepayment';
import {
  resolveCohortDraw,
  resolveEffectiveRevSharePct,
} from './resolvePlanFunding';

function accrueMonthlyFee(
  outstandingPrincipal,
  outstandingFee,
  monthlyFeePct,
  additionalBalance = 0
) {
  const monthStartBalance = Math.max(
    0,
    outstandingPrincipal + outstandingFee + additionalBalance
  );
  if (monthStartBalance <= 0 || monthlyFeePct <= 0) {
    return 0;
  }
  return Math.round((monthStartBalance * monthlyFeePct) / 100);
}

export function applyRepaymentCapacity({
  month,
  capacity,
  outstandingPrincipal,
  outstandingFee,
  repaymentsByMonth,
  principalRepaymentsByMonth,
  feeRepaymentsByMonth,
}) {
  let remainingCapacity = capacity;
  let nextOutstandingPrincipal = outstandingPrincipal;
  let nextOutstandingFee = outstandingFee;

  if (remainingCapacity > 0 && nextOutstandingPrincipal > 0) {
    const principalPay = Math.min(remainingCapacity, nextOutstandingPrincipal);
    repaymentsByMonth[month] = (repaymentsByMonth[month] || 0) + principalPay;
    principalRepaymentsByMonth[month] =
      (principalRepaymentsByMonth[month] || 0) + principalPay;
    nextOutstandingPrincipal -= principalPay;
    remainingCapacity -= principalPay;
  }

  if (
    remainingCapacity > 0 &&
    nextOutstandingPrincipal <= 0 &&
    nextOutstandingFee > 0
  ) {
    const feePay = Math.min(remainingCapacity, nextOutstandingFee);
    repaymentsByMonth[month] = (repaymentsByMonth[month] || 0) + feePay;
    feeRepaymentsByMonth[month] = (feeRepaymentsByMonth[month] || 0) + feePay;
    nextOutstandingFee -= feePay;
  }

  return {
    outstandingPrincipal: nextOutstandingPrincipal,
    outstandingFee: nextOutstandingFee,
  };
}

export function calcSingleCohortRepayments({
  drawMonth,
  drawAmount,
  marketing,
  revSharePct,
  cumulativeRoas,
  gracePeriod,
  monthlyFeePct,
  maxRepaymentPeriods = null,
  resolveAdditionalFeeAccrualBase = null,
}) {
  const repaymentsByMonth = {};
  const principalRepaymentsByMonth = {};
  const feeRepaymentsByMonth = {};
  const feeAccrualByMonth = {};
  if (drawAmount <= 0 || drawMonth < 0) {
    return {
      repaymentsByMonth,
      principalRepaymentsByMonth,
      feeRepaymentsByMonth,
      feeAccrualByMonth,
      lastMonth: drawMonth,
    };
  }

  const roas = applyRepaymentPeriodLimit(
    cumulativeRoas,
    maxRepaymentPeriods ?? cumulativeRoas.length
  );
  const maxRepaymentPeriodCount = roas.length;
  const marginal = buildMarginal(roas);
  const finalMonth = drawMonth + gracePeriod + maxRepaymentPeriodCount - 1;
  let outstandingPrincipal = drawAmount;
  let outstandingFee = 0;
  let lastMonth = drawMonth;

  for (let month = drawMonth; month <= finalMonth; month += 1) {
    lastMonth = month;

    const additionalBalance = resolveAdditionalFeeAccrualBase
      ? resolveAdditionalFeeAccrualBase(month)
      : 0;
    const monthlyFeeAccrual = accrueMonthlyFee(
      outstandingPrincipal,
      outstandingFee,
      monthlyFeePct,
      additionalBalance
    );
    if (monthlyFeeAccrual > 0) {
      outstandingFee += monthlyFeeAccrual;
      feeAccrualByMonth[month] = monthlyFeeAccrual;
    }

    let capacity = 0;
    if (month >= drawMonth + gracePeriod) {
      const k = month - drawMonth - gracePeriod;
      if (k >= 0 && k < maxRepaymentPeriodCount) {
        const cohortRevenue = (marketing * (marginal[k] ?? 0)) / 100;
        capacity = Math.round((cohortRevenue * revSharePct) / 100);
      }
    }

    ({ outstandingPrincipal, outstandingFee } = applyRepaymentCapacity({
      month,
      capacity,
      outstandingPrincipal,
      outstandingFee,
      repaymentsByMonth,
      principalRepaymentsByMonth,
      feeRepaymentsByMonth,
    }));

    if (outstandingPrincipal <= 0 && outstandingFee <= 0) {
      return {
        repaymentsByMonth,
        principalRepaymentsByMonth,
        feeRepaymentsByMonth,
        feeAccrualByMonth,
        lastMonth,
      };
    }
  }

  if (outstandingPrincipal > 0) {
    repaymentsByMonth[finalMonth] =
      (repaymentsByMonth[finalMonth] || 0) + outstandingPrincipal;
    principalRepaymentsByMonth[finalMonth] =
      (principalRepaymentsByMonth[finalMonth] || 0) + outstandingPrincipal;
    outstandingPrincipal = 0;
  }

  if (outstandingFee > 0) {
    repaymentsByMonth[finalMonth] =
      (repaymentsByMonth[finalMonth] || 0) + outstandingFee;
    feeRepaymentsByMonth[finalMonth] =
      (feeRepaymentsByMonth[finalMonth] || 0) + outstandingFee;
    outstandingFee = 0;
  }

  return {
    repaymentsByMonth,
    principalRepaymentsByMonth,
    feeRepaymentsByMonth,
    feeAccrualByMonth,
    lastMonth,
  };
}

export function calcAllCohortRepayments(
  state,
  numCohorts,
  spendRoasVectors = null,
  facilityFeeContext = null
) {
  const monthlyFeePct = resolveMonthlyFeePct(state.feeType, state.feePct);
  const gracePeriod = Math.max(0, Number(state.gracePeriod) || 0);

  const cohortRepaymentsByMonth = Array.from({ length: numCohorts }, () => ({}));
  const cohortPrincipalRepaymentsByMonth = Array.from(
    { length: numCohorts },
    () => ({})
  );
  const cohortFeeRepaymentsByMonth = Array.from({ length: numCohorts }, () => ({}));
  const cohortFeeAccrualByMonth = Array.from({ length: numCohorts }, () => ({}));
  const cohortTotalFee = new Array(numCohorts).fill(0);
  let maxRepaymentMonth = 0;
  const facilityFeeAccrualApplied = new Set();
  const resolveAdditionalFeeAccrualBase = (month) => {
    if (!facilityFeeContext) {
      return 0;
    }
    const { firstDrawMonth, facilityFeeAmount, facilityFeePaymentMonth } =
      facilityFeeContext;
    if (
      facilityFeeAmount <= 0 ||
      firstDrawMonth < 0 ||
      month < firstDrawMonth ||
      month >= facilityFeePaymentMonth
    ) {
      return 0;
    }
    if (facilityFeeAccrualApplied.has(month)) {
      return 0;
    }
    facilityFeeAccrualApplied.add(month);
    return facilityFeeAmount;
  };

  const repaymentPeriodOverride =
    state.numberOfRepayments != null && Number(state.numberOfRepayments) > 0
      ? Math.max(1, Math.floor(Number(state.numberOfRepayments)))
      : null;

  for (let i = 0; i < numCohorts; i++) {
    const drawMonth = i + 1;
    const drawAmount = resolveCohortDraw(state, i);
    const rawVector = [...(spendRoasVectors?.[i] ?? state.roas)];
    const effectiveRepaymentPeriods =
      repaymentPeriodOverride ?? rawVector.length;
    const cumulativeRoas = resolveRepaymentRoasVector(
      rawVector,
      effectiveRepaymentPeriods,
      {
        drawAmount,
        marketing: state.marketing[i],
        revSharePct: resolveEffectiveRevSharePct(state, i),
        gracePeriod,
        monthlyFeePct,
      }
    );
    const {
      repaymentsByMonth,
      principalRepaymentsByMonth,
      feeRepaymentsByMonth,
      feeAccrualByMonth,
      lastMonth,
    } = calcSingleCohortRepayments({
      drawMonth,
      drawAmount,
      marketing: state.marketing[i],
      revSharePct: resolveEffectiveRevSharePct(state, i),
      cumulativeRoas,
      gracePeriod,
      monthlyFeePct,
      maxRepaymentPeriods: effectiveRepaymentPeriods,
      resolveAdditionalFeeAccrualBase,
    });
    cohortRepaymentsByMonth[i] = repaymentsByMonth;
    cohortPrincipalRepaymentsByMonth[i] = principalRepaymentsByMonth;
    cohortFeeRepaymentsByMonth[i] = feeRepaymentsByMonth;
    cohortFeeAccrualByMonth[i] = feeAccrualByMonth;
    cohortTotalFee[i] = Object.values(feeAccrualByMonth).reduce(
      (sum, amount) => sum + amount,
      0
    );
    maxRepaymentMonth = Math.max(maxRepaymentMonth, lastMonth);
  }

  return {
    cohortRepaymentsByMonth,
    cohortPrincipalRepaymentsByMonth,
    cohortFeeRepaymentsByMonth,
    cohortFeeAccrualByMonth,
    cohortTotalFee,
    maxRepaymentMonth,
    monthlyFeePct,
  };
}
